import React, { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useToast } from '@/hooks/use-toast';
import { useAuth } from '@/contexts/AuthContext';
import { DocumentService } from '@/integrations/supabase/services';
import { FirebaseStorageService } from '@/integrations/firebase/storage';
import { useNavigate } from 'react-router-dom';

// Document types
interface Document {
  id?: string;
  user_id: string;
  file_name: string;
  file_size: number;
  document_type: string;
  file_url: string;
  uploaded_at: string;
  status: string;
}

interface DocumentInsert {
  user_id: string;
  file_name: string;
  file_size: number;
  document_type: string;
  file_url: string;
  uploaded_at: string;
  status: string;
}
import { Upload, FileText, Check, X, ChevronRight } from 'lucide-react';
import { useCompletionStatus } from '@/hooks/useCompletionStatus';
import { DashboardLayout } from '@/components/DashboardLayout';
import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils';

const Documents = () => {
  const [documents, setDocuments] = useState<Document[]>([]);
  const [loading, setLoading] = useState(true);
  const [uploadingDocument, setUploadingDocument] = useState<string | null>(null);
  const { user } = useAuth();
  const { toast } = useToast();
  const navigate = useNavigate();
  const { completionStatus, loading: statusLoading } = useCompletionStatus();

  const requiredDocuments = [
    { type: 'passport', label: 'Passport Copy', description: 'Clear copy of all pages' },
    { type: 'photo', label: 'Passport Photos', description: 'Recent passport-style photo' },
    { type: 'application_form', label: 'Application Form', description: 'Completed Residence Card application' },
    { type: 'accommodation', label: 'Proof of Accommodation', description: 'Rental agreement or property deed' },
    { type: 'health_insurance', label: 'Health Insurance', description: 'Valid health insurance coverage' },
    { type: 'financial_proof', label: 'Financial Documentation', description: 'Bank statements or income proof' },
    { type: 'purpose_documents', label: 'Purpose-specific Documents', description: 'Work contract, study acceptance, etc.' },
    { type: 'cv', label: 'Curriculum Vitae (CV)', description: 'Upload your latest CV (PDF)' }
  ];

  useEffect(() => {
    if (user) {
      loadDocuments();
    }
  }, [user]);

  const loadDocuments = async () => {
    if (!user) return;
    
    try {
      const data = await DocumentService.getByUserId(user.uid);
      setDocuments(data || []);
    } catch (error) {
      console.error('Error loading documents:', error);
      toast({
        title: "Error loading documents",
        description: "Could not load your documents.",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>, documentType: string) => {
    if (!user || !e.target.files || e.target.files.length === 0) return;
    
    const file = e.target.files[0];
    
    // Validate file type
    const allowedTypes = ['application/pdf', 'image/jpeg', 'image/png'];
    if (!allowedTypes.includes(file.type)) {
      toast({
        title: "Invalid file type",
        description: "Please upload PDF, JPEG, or PNG files only.",
        variant: "destructive",
      });
      return;
    }

    // Validate file size (5MB limit)
    const maxSize = 5 * 1024 * 1024; // 5MB in bytes
    if (file.size > maxSize) {
      toast({
        title: "File too large",
        description: "Please upload a file smaller than 5MB.",
        variant: "destructive",
      });
      return;
    }

    setUploadingDocument(documentType);

    try {
      // Check if document already exists
      const existingDoc = documents.find(doc => doc.document_type === documentType);
      
      // Upload file to Supabase Storage
      const uploadResult = await FirebaseStorageService.uploadFile(file, user.uid, 'document', documentType);
      const { url, path } = uploadResult;

      // Save document reference in database
      const documentData: DocumentInsert = {
        user_id: user.uid,
        file_name: file.name,
        file_size: file.size,
        document_type: documentType,
        file_url: url,
        uploaded_at: new Date().toISOString(),
        status: 'pending'
      };

      if (existingDoc) {
        // Update existing document
        await DocumentService.update(existingDoc.id!, documentData);
      } else {
        // Insert new document
        await DocumentService.add(documentData);
      }

      toast({
        title: existingDoc ? "Document replaced" : "Document uploaded",
        description: `Your document has been successfully ${existingDoc ? 'replaced' : 'uploaded'}.`,
      });

      // Reload documents to get the latest data
      loadDocuments();
    } catch (error) {
      console.error('Error uploading document:', error);
      toast({
        title: "Error uploading document",
        description: "Please try again later.",
        variant: "destructive",
      });
    } finally {
      setUploadingDocument(null);
    }
  };

  const handleDeleteDocument = async (documentId: string, filePath: string) => {
    if (!user) return;

    try {
      // Delete file from Supabase Storage
      await FirebaseStorageService.deleteFile(filePath);

      // Delete record from database
      await DocumentService.delete(documentId);

      toast({
        title: "Document deleted",
        description: "Your document has been successfully deleted.",
      });

      loadDocuments();
    } catch (error) {
      console.error('Error deleting document:', error);
      toast({
        title: "Error deleting document",
        description: "Please try again later.",
        variant: "destructive",
      });
    }
  };

  const getDocumentStatus = (documentType: string) => {
    return documents.find(doc => doc.document_type === documentType);
  };

  const handleContinue = () => {
    const uploadedCount = requiredDocuments.filter(doc => getDocumentStatus(doc.type)).length;
    
    if (uploadedCount < requiredDocuments.length) {
      toast({
        title: "Missing documents",
        description: `Please upload all ${requiredDocuments.length} required documents before continuing.`,
        variant: "destructive",
      });
      return;
    }

    navigate('/application-form');
  };

  if (loading) {
    return (
      <DashboardLayout pageTitle="Document Upload">
        <div className="text-center">Loading...</div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout pageTitle="Document Upload">
      <Card>
        <CardHeader>
          <CardTitle>Document Upload</CardTitle>
          <CardDescription>
            Upload the required documents for your Residence card application
          </CardDescription>
        </CardHeader>
        
        <CardContent className="space-y-6">
          {/* Mobile Progress Bar */}
          <div className="sm:hidden">
            <div className="flex items-center justify-between mb-2">
              <h3 className="text-sm font-medium text-gray-900">Upload Progress</h3>
              <span className="text-sm font-semibold text-blue-600">
                {Math.round((documents.length / requiredDocuments.length) * 100)}%
              </span>
            </div>
            <div className="w-full bg-gray-100 rounded-full h-1.5">
              <div 
                className="bg-blue-600 h-1.5 rounded-full transition-all duration-300"
                style={{ width: `${(documents.length / requiredDocuments.length) * 100}%` }}
              />
            </div>
            <p className="mt-1 text-xs text-gray-500 text-center">
              {documents.length} of {requiredDocuments.length} documents uploaded
            </p>
          </div>

          {/* Document Cards */}
          <div className="grid gap-4">
            {requiredDocuments.map((doc) => {
              const uploadedDoc = getDocumentStatus(doc.type);
              const isUploaded = !!uploadedDoc;

              return (
                <div 
                  key={doc.type} 
                  className={cn(
                    "relative overflow-hidden rounded-xl border transition-all duration-200",
                    isUploaded ? "bg-green-50/50 border-green-100" : "bg-white border-gray-100",
                    "hover:shadow-md"
                  )}
                >
                  <div className="p-4">
                    {/* Document Header */}
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center space-x-3">
                        <div 
                          className={cn(
                            "flex-shrink-0 rounded-lg p-2",
                            isUploaded ? "bg-green-100" : "bg-gray-50"
                          )}
                        >
                          {isUploaded ? (
                            <Check className="h-5 w-5 text-green-600" />
                          ) : (
                            <FileText className="h-5 w-5 text-gray-600" />
                          )}
                        </div>
                        <div>
                          <h3 className="text-sm font-semibold text-gray-900">
                            {doc.label}
                          </h3>
                        </div>
                      </div>
                      <Label 
                        htmlFor={`file-${doc.type}`}
                        className={cn(
                          "inline-flex items-center justify-center rounded-full px-4 py-1.5 text-xs font-medium transition-colors",
                          isUploaded 
                            ? "bg-green-100 text-green-700 hover:bg-green-200" 
                            : "bg-blue-600 text-white hover:bg-blue-700",
                          "cursor-pointer touch-manipulation"
                        )}
                      >
                        {isUploaded ? 'Replace' : 'Upload'}
                      </Label>
                      <Input
                        id={`file-${doc.type}`}
                        type="file"
                        accept=".pdf,.jpg,.jpeg,.png"
                        onChange={(e) => handleFileUpload(e, doc.type)}
                        className="hidden"
                        disabled={!!uploadingDocument}
                      />
                      {uploadingDocument === doc.type && (
                        <div className="mt-2 flex items-center space-x-2 text-sm text-gray-500">
                          <div className="h-4 w-4 animate-spin rounded-full border-2 border-blue-600 border-t-transparent"></div>
                          <span>Uploading document...</span>
                        </div>
                      )}
                    </div>

                    {/* Document Details */}
                    <div className="space-y-2">
                      <p className="text-xs text-gray-600 leading-relaxed">
                        {doc.description}
                      </p>
                      {isUploaded && (
                        <div className="flex items-center mt-2 bg-gray-50 rounded-lg p-2">
                          <FileText className="h-4 w-4 text-gray-500 mr-2" />
                          <span className="text-xs text-gray-600 truncate">
                            {uploadedDoc.file_name}
                          </span>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Desktop Progress Section */}
          <div className="hidden sm:block border-t pt-6">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h3 className="font-medium text-gray-900">Upload Progress</h3>
                <p className="text-sm text-gray-600">
                  {documents.length} of {requiredDocuments.length} documents uploaded
                </p>
              </div>
              <div className="text-right">
                <div className="text-2xl font-bold text-blue-600">
                  {Math.round((documents.length / requiredDocuments.length) * 100)}%
                </div>
                <div className="text-sm text-gray-600">Complete</div>
              </div>
            </div>
            
            <div className="w-full bg-gray-200 rounded-full h-2">
              <div 
                className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                style={{ width: `${(documents.length / requiredDocuments.length) * 100}%` }}
              />
            </div>
          </div>

          {/* Navigation Buttons */}
          <div className="flex space-x-4 pt-4 sm:pt-6">
            <Button 
              type="button" 
              variant="outline" 
              onClick={() => navigate('/personal-info')}
              className="w-full"
            >
              Back
            </Button>
            <Button 
              onClick={handleContinue}
              className="w-full"
              disabled={documents.length < requiredDocuments.length}
            >
              Continue
            </Button>
          </div>
        </CardContent>
      </Card>
    </DashboardLayout>
  );
};

export default Documents;
